import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

/**
 * Compass PM — Privacy Policy
 * Public, link-reachable page required by Apple App Store Connect and Google Play Console.
 * Covers: JWT auth, Google OAuth, Stripe billing, LLM processing, mobile push.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-200">
      <header className="border-b border-slate-800/70 bg-slate-950/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-white" data-testid="privacy-back-home">
            <ArrowLeft size={16} /> Compass PM
          </Link>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80">
            <Shield size={12} /> Privacy
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-main">
        <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "Outfit" }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-10">Last updated: 5 February 2026</p>

        <section className="space-y-6 text-sm leading-relaxed text-slate-300">
          <p>
            Compass PM ("<strong>Compass PM</strong>", "we", "our", "us") is operated by Uplift Communications Corporation.
            This policy explains what personal data we collect, why we collect it, who processes it, and the
            rights you have over it. It applies to the Compass PM web app at
            {" "}<code className="text-blue-300">app.compasspm.com</code>, the Compass PM mobile apps for iOS and
            Android, and any communications we send you (billing receipts, slippage alerts, Monday weekly status
            emails, mobile push notifications).
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>1. What we collect</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Account:</strong> name, email, hashed password (bcrypt), currency preference, account type (personal / enterprise), organization ID.</li>
            <li><strong>Google Sign-In (optional):</strong> if you authenticate with Google, we receive your Google-verified email and display name only. We never read your Google Drive, Gmail, Calendar, or any other Google service.</li>
            <li><strong>Project data you enter:</strong> project plans, tasks, dependencies, estimates, actuals, baselines, risks, teammate names, portfolio and program metadata, uploaded CSVs.</li>
            <li><strong>Billing (if you subscribe):</strong> Stripe customer ID, subscription status, seat count. We never see or store your card number — Stripe tokenizes it on its own servers.</li>
            <li><strong>Mobile push token (optional):</strong> if you install the Compass PM mobile app and grant notification permission, your device's Expo Push Token (an opaque Apple/Google-issued identifier) is stored against your account solely to deliver slippage alerts.</li>
            <li><strong>Operational logs:</strong> request IDs, approximate IP, user-agent, and timestamps retained for 30 days for abuse-prevention and support.</li>
          </ul>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>2. How we use it</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Operating Compass PM features (Gantt scheduling, baselines, risk scoring, exports, share links).</li>
            <li>Sending transactional email: slippage alerts, Monday morning status packs, invites, billing receipts, password resets. We do not send marketing email without separate consent.</li>
            <li>Delivering mobile push notifications when a project slips past its stated due date.</li>
            <li>Processing payments and seat changes through Stripe.</li>
            <li>Generating AI content you request — Executive Summary, Suggest Risks, Mitigation Coach — by sending the minimum necessary project context to our LLM provider (see §4).</li>
          </ul>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>3. Authentication &amp; session security</h2>
          <p>
            Passwords are stored as bcrypt hashes — never in plaintext — and we never log them. Logged-in sessions
            use a signed JWT delivered as an <code className="text-blue-300">HttpOnly</code>, <code className="text-blue-300">Secure</code>,
            <code className="text-blue-300"> SameSite=Lax</code> cookie so it is not accessible to third-party scripts. If you choose
            Google Sign-In, authentication is brokered by the Emergent Managed Google Auth integration; the OAuth
            handshake takes place on Google's servers and only the resulting email + name are returned to us.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>4. AI / LLM processing</h2>
          <p>
            Our AI features (Executive Summary, Suggest Risks, Mitigation Coach) call large-language-model providers
            (OpenAI, Anthropic Claude, Google Gemini) through the Emergent LLM proxy. Only the specific project
            context required for the prompt is sent — for example, a risk description and the current risk score —
            and the request is routed server-to-server using Emergent-managed credentials. Provider policies prohibit
            training on API traffic by default. We do not share teammate PII, billing data, or passwords with any
            LLM provider.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>5. Payments (Stripe)</h2>
          <p>
            All subscription payments are processed by Stripe, Inc. We rely on Stripe Checkout and the Stripe Billing
            Portal, which means your card number is captured directly by Stripe and never touches Compass PM
            servers. We store your Stripe customer ID, subscription status, price lookup key, and seat count. Stripe's
            own privacy policy applies to the card data and is available at <a className="text-blue-400 underline"
            href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>6. Mobile push notifications</h2>
          <p>
            The Compass PM mobile app uses the Expo Push service (operated by Expo, Inc.) to deliver notifications
            through Apple's APNs and Google's FCM. If you decline the system notification prompt, no token is ever
            stored and no notifications are sent. If you later want to revoke notifications, disable them in the
            Compass PM app's system settings on your device, or use the <em>Delete my account</em> flow (§9) to purge
            your token entirely.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>7. Sub-processors</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>MongoDB Atlas</strong> — primary application database (encrypted at rest and in transit).</li>
            <li><strong>Stripe, Inc.</strong> — payment processing and billing portal.</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>Expo, Inc.</strong> — mobile push dispatch to APNs / FCM.</li>
            <li><strong>Emergent LLM proxy</strong> — routes AI prompts to OpenAI / Anthropic / Google providers.</li>
            <li><strong>Google LLC</strong> — Google Sign-In (only when you choose it).</li>
          </ul>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>8. Data retention</h2>
          <p>
            We retain your project data for as long as your account is active. Operational logs are rotated after
            30 days. When you delete your account (§9), all of your project, task, risk, teammate, invite, audit,
            branding, and push-token records are permanently removed from our live database within 24 hours.
            Stripe is instructed to cancel any active subscription at that time. Backups are automatically rotated
            and all deleted records are purged from backups within 30 days.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>9. Your rights &amp; account deletion</h2>
          <p>
            You can access, export, and permanently delete your account at any time from
            {" "}<Link className="text-blue-400 underline" to="/app/settings">Settings &rarr; Danger Zone &rarr; Delete my account</Link>
            {" "}inside the app. You can also email <a className="text-blue-400 underline" href="mailto:privacy@upliftc.com">privacy@upliftc.com</a> and
            we will complete the deletion within 7 days. Residents of the EU, UK, California, Virginia, and other
            jurisdictions with similar laws have the right to request access, correction, portability, or deletion
            of their personal data; those requests are honoured without discrimination.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>10. Children</h2>
          <p>Compass PM is a business tool and is not directed to anyone under 13. We do not knowingly collect data from children.</p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>11. Changes</h2>
          <p>
            We will update the date at the top of this page whenever we make a material change and, if you are a
            paying customer, email you a summary at least 7 days before the change takes effect.
          </p>

          <h2 className="text-xl font-semibold text-white pt-4" style={{ fontFamily: "Outfit" }}>12. Contact</h2>
          <p>
            Uplift Communications Corporation — <a className="text-blue-400 underline" href="mailto:privacy@upliftc.com">privacy@upliftc.com</a>.
            For EU residents, data-protection enquiries can be sent to the same address.
          </p>
        </section>

        <footer className="mt-16 border-t border-slate-800/70 pt-6 text-xs text-slate-500">
          © 2026 Uplift Communications Corporation · Compass PM ·
          {" "}<Link to="/" className="text-slate-400 hover:text-white" data-testid="privacy-home-link">Home</Link> ·
          {" "}<Link to="/contact" className="text-slate-400 hover:text-white" data-testid="privacy-contact-link">Contact</Link>
        </footer>
      </main>
    </div>
  );
}
